import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { LuGrab, LuHand } from 'react-icons/lu';
import { Dicom } from '@/models/dicom';
import styles from './DicomList.module.css';
import { DicomItem } from './DicomItem';
import { useViewerContext } from '../Viewer/ViewerContext';
import { useThumbnailsGate } from './useThumbnailsGate';

// Thumbnail packing as the (resizable) bar grows. Thumbnails grow up to
// MAX_THUMB_H; a new row is added once there's room for another MIN_ROW_H row,
// then the thumbnails grow again — capped at max — and so on (column-major).
const MIN_ROW_H = 84;
const MAX_THUMB_H = 180;
const THUMB_ASPECT = 1.4; // width / height
const MAX_ROWS = 8;

// Pan tuning: movement (px) before a press becomes a pan rather than a click,
// plus the inertia (per-frame velocity decay and the coast cutoff).
const DRAG_THRESHOLD = 5;
const FRICTION = 0.95;
const MIN_VELOCITY = 0.5;

// How long the hint "flashes" after a successful drag-to-pane (ms).
const FLASH_DURATION = 250;

// Drag-to-pane modifier shown in the hint. Shift works uniformly on every
// platform: it doesn't suppress HTML5 dragstart (unlike Cmd on macOS) and has
// no window-move conflict (unlike Alt on many Linux WMs).
const DRAG_MODIFIER = '⇧ Shift';

interface DicomListProps {
  dicoms: Dicom[];
}

export default function DicomList({ dicoms }: DicomListProps) {
  const {
    filteredSortedDicoms,
    navigateToDicomByUuid,
    currentGroupIndex,
    activeDicomUuids,
    pinnedPanes,
    allPanesPinned,
    dicomNumberByUuid,
    studyUuid,
  } = useViewerContext();

  // 'thumbnails' readiness gate (AP-778 §6) — see useThumbnailsGate.
  const onThumbSettled = useThumbnailsGate(studyUuid, filteredSortedDicoms);

  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const stripRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Recomputed whenever the strip is resized (the bar is user-draggable).
  const [thumbLayout, setThumbLayout] = useState({
    rows: 1,
    thumbH: 100,
    thumbW: Math.round(100 * THUMB_ASPECT),
  });
  // Whether the rail overflows horizontally — gates the hint's visibility.
  const [isOverflowing, setIsOverflowing] = useState(false);
  // Whether the drag-to-pane modifier is held — lights the modifier chip in the hint.
  const [modifierHeld, setModifierHeld] = useState(false);
  // Live hint feedback for the modifier drag-to-pane gesture: lit while dragging,
  // then a brief flash on a successful drop.
  const [isPinning, setIsPinning] = useState(false);
  const [flash, setFlash] = useState(false);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const sync = (e: KeyboardEvent) => setModifierHeld(e.shiftKey);
    const reset = () => setModifierHeld(false);
    window.addEventListener('keydown', sync);
    window.addEventListener('keyup', sync);
    window.addEventListener('blur', reset);
    return () => {
      window.removeEventListener('keydown', sync);
      window.removeEventListener('keyup', sync);
      window.removeEventListener('blur', reset);
    };
  }, []);

  // Measure the strip and recompute the row count + thumbnail size. Reads the
  // gap and padding from the resolved styles (not hardcoded constants) so the
  // math tracks the active theme — compact mode redefines --space-sm/--space-md.
  const measureStrip = useCallback((strip: HTMLDivElement) => {
    setIsOverflowing(strip.scrollWidth - strip.clientWidth > 1);
    const style = getComputedStyle(strip);
    const gap = parseFloat(style.rowGap);
    const padding =
      parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    // Use offsetHeight (stable border-box), NOT clientHeight: clientHeight
    // shrinks when the horizontal scrollbar appears, which would feed back into
    // thumb sizing and flicker the scrollbar on/off near the overflow threshold.
    const availH = strip.offsetHeight - padding;
    // Bail if unmeasured (0 height) or styles unresolved (NaN, e.g. in jsdom).
    if (!(availH > 0)) return;
    const rows = Math.min(
      MAX_ROWS,
      Math.max(1, Math.floor((availH + gap) / (MIN_ROW_H + gap)))
    );
    const thumbH = Math.min(
      MAX_THUMB_H,
      Math.floor((availH - (rows - 1) * gap) / rows)
    );
    const thumbW = Math.round(thumbH * THUMB_ASPECT);
    setThumbLayout((prev) =>
      prev.rows === rows && prev.thumbH === thumbH && prev.thumbW === thumbW
        ? prev
        : { rows, thumbH, thumbW }
    );
  }, []);

  // Callback ref: (re)attach the ResizeObserver each time the strip node mounts,
  // detach when it unmounts. The strip is conditionally rendered (it's gone when
  // the view filter matches nothing), so a one-time mount effect would never
  // re-observe the fresh node once the filter is cleared.
  const setStripRef = useCallback(
    (node: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      stripRef.current = node;
      if (!node) return;
      measureStrip(node);
      const ro = new ResizeObserver(() => measureStrip(node));
      ro.observe(node);
      resizeObserverRef.current = ro;
    },
    [measureStrip]
  );

  // Re-check overflow when the content (filter) or thumbnail size changes: these
  // alter scrollWidth, which the ResizeObserver (box-size only) doesn't catch.
  // The height-derived layout is unchanged here, so we deliberately don't
  // recompute it — recomputing would be redundant and risks a re-measure loop.
  useEffect(() => {
    const strip = stripRef.current;
    if (strip) setIsOverflowing(strip.scrollWidth - strip.clientWidth > 1);
  }, [filteredSortedDicoms.length, thumbLayout]);

  // A plain left-drag pans the rail horizontally. A Shift-drag is left untouched
  // so the thumbnail's native HTML5 drag (drag-to-pane) runs.
  const isScrolling = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);
  const hasDragged = useRef(false);
  // Momentum (inertia) state for coasting after a pan release.
  const velocity = useRef(0);
  const lastClientX = useRef(0);
  const momentumRaf = useRef(0);

  const pinnedUuids = useMemo(
    () => new Set([...pinnedPanes.values()].map((d) => d.uuid)),
    [pinnedPanes]
  );

  // First active DICOM on the current page (for auto-scroll target).
  const firstActiveDicom = useMemo(
    () => filteredSortedDicoms.find((d) => activeDicomUuids.has(d.uuid)),
    [filteredSortedDicoms, activeDicomUuids]
  );

  const coastWithMomentum = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;

    velocity.current *= FRICTION;
    if (Math.abs(velocity.current) < MIN_VELOCITY) {
      velocity.current = 0;
      return;
    }

    strip.scrollLeft -= velocity.current;
    momentumRaf.current = requestAnimationFrame(coastWithMomentum);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    hasDragged.current = false;
    cancelAnimationFrame(momentumRaf.current); // stop any active coasting
    // Pan only on a clean primary drag. Any modifier (Shift is the advertised
    // drag-to-pane trigger) is left to the native drag, and other buttons
    // (right → context menu, middle) keep their behavior.
    if (e.button !== 0 || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey)
      return;
    const strip = stripRef.current;
    if (!strip) return;
    isScrolling.current = true;
    velocity.current = 0;
    startX.current = e.clientX;
    scrollStart.current = strip.scrollLeft;
    lastClientX.current = e.clientX;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isScrolling.current) return;
    const strip = stripRef.current;
    if (!strip) return;

    // Below the threshold the press is still a click (so a click with a little
    // jitter navigates instead of being swallowed as a pan). Once it's a drag,
    // stay a drag for the rest of the gesture.
    if (
      !hasDragged.current &&
      Math.abs(e.clientX - startX.current) < DRAG_THRESHOLD
    ) {
      return;
    }
    hasDragged.current = true;

    // Velocity ≈ the last frame's delta (mousemove fires ~once per frame), used
    // for the coast on release.
    velocity.current = e.clientX - lastClientX.current;
    lastClientX.current = e.clientX;

    strip.scrollLeft = scrollStart.current - (e.clientX - startX.current);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isScrolling.current) return;
    isScrolling.current = false;
    // Coast if the pointer was still moving fast enough at release.
    if (Math.abs(velocity.current) > MIN_VELOCITY) {
      momentumRaf.current = requestAnimationFrame(coastWithMomentum);
    }
  }, [coastWithMomentum]);

  // Plain drag → cancel the native drag so the rail pans. Shift-drag → the
  // native drag-to-pane runs; light up the hint while it's in flight.
  const handleDragStart = useCallback((e: React.DragEvent) => {
    if (isScrolling.current) {
      e.preventDefault();
    } else {
      setIsPinning(true);
    }
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    setIsPinning(false);
    // Flash the hint only when the drag actually dropped onto a pane.
    if (e.dataTransfer.dropEffect === 'none') return;
    setFlash(true);
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = setTimeout(() => setFlash(false), FLASH_DURATION);
  }, []);

  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    // Suppress the click that follows a pan so it doesn't navigate.
    if (hasDragged.current) {
      e.stopPropagation();
      hasDragged.current = false;
    }
  }, []);

  // Stop any in-flight momentum animation / flash timer when the component unmounts.
  useEffect(
    () => () => {
      cancelAnimationFrame(momentumRaf.current);
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    },
    []
  );

  // Auto-scroll to first visible (non-pinned) DICOM on page change.
  // Use 'instant' on initial mount (new study) to avoid misleading animation,
  // then 'smooth' for subsequent group navigation within the same study.
  const hasScrolledRef = useRef(false);
  const lastScrollTimeRef = useRef(0);
  useEffect(() => {
    if (!firstActiveDicom) return;

    const strip = stripRef.current;
    const el = itemRefs.current.get(firstActiveDicom.uuid);
    if (!strip || !el) return;

    // Snap instantly when pages arrive rapidly (holding the arrow); overlapping
    // smooth animations would make the reel jitter. Isolated presses animate.
    const now = performance.now();
    const isRapid = now - lastScrollTimeRef.current < 250;
    lastScrollTimeRef.current = now;

    // Center the active clip by scrolling the rail directly. scrollIntoView
    // no-ops when the target is already on-screen (common with small pinned
    // page sizes), which left the reel stuck.
    const elRect = el.getBoundingClientRect();
    const stripRect = strip.getBoundingClientRect();
    const delta =
      elRect.left - stripRect.left + elRect.width / 2 - strip.clientWidth / 2;
    strip.scrollBy?.({
      left: delta,
      behavior: hasScrolledRef.current && !isRapid ? 'smooth' : 'auto',
    });
    hasScrolledRef.current = true;
  }, [currentGroupIndex, firstActiveDicom]);

  if (dicoms.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          No DICOM files available for this study
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {filteredSortedDicoms.length === 0 ? (
        <div className={styles.emptyState}>
          No views match the current filter
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            ref={setStripRef}
            className={styles.thumbnailStrip}
            style={
              {
                '--rows': String(thumbLayout.rows),
                '--thumb-h': `${thumbLayout.thumbH}px`,
                '--thumb-w': `${thumbLayout.thumbW}px`,
              } as CSSProperties
            }
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onClickCapture={handleClickCapture}
          >
            {filteredSortedDicoms.map((dicom) => {
              const isActive = activeDicomUuids.has(dicom.uuid);
              const isPinned = pinnedUuids.has(dicom.uuid);

              return (
                <div
                  key={dicom.uuid}
                  ref={(el) => {
                    if (el) itemRefs.current.set(dicom.uuid, el);
                    else itemRefs.current.delete(dicom.uuid);
                  }}
                >
                  <DicomItem
                    dicom={dicom}
                    displayNumber={dicomNumberByUuid.get(dicom.uuid) ?? 0}
                    isActive={isActive}
                    isPinned={isPinned}
                    // Fully pinned grid: only thumbnails of pinned DICOMs can act
                    // (focus their pane); the rest stay browsable but can't load.
                    isNavigationBlocked={allPanesPinned && !isPinned}
                    onClick={() => navigateToDicomByUuid(dicom.uuid)}
                    onThumbnailSettled={() => onThumbSettled(dicom.uuid)}
                  />
                </div>
              );
            })}
          </div>
          {isOverflowing && (
            <div
              className={styles.dragHint}
              data-active={isPinning || flash || undefined}
              data-testid="drag-hint"
              aria-hidden="true"
            >
              <kbd
                className={styles.kbd}
                data-active={modifierHeld || undefined}
              >
                {DRAG_MODIFIER}
              </kbd>
              <span className={styles.plus}>+</span>
              {isPinning ? (
                <LuGrab className={styles.grabIcon} data-active />
              ) : (
                <LuHand
                  className={styles.grabIcon}
                  data-active={flash || undefined}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
